import React, { useCallback, useRef } from 'react'
import { useQuery } from 'react-query'
import { ENTITY } from '../constants'
import { fetchStatByDocumentTypes } from '../api'
import { StatByDocument, StatByDocumentsType } from '../mappers'
import Skeleton from 'react-loading-skeleton'
import { useTranslation } from 'react-i18next'
import classes from '../styles/stat-card.module.scss'
import { useHistory } from 'react-router'

interface Props {
    params?: Record<string, any>
}

const Chart = (window as any).Chart

export function StatCard({ params = {} }: Props) {
    const { t, i18n } = useTranslation()
    const chartRef = useRef<any>(undefined)
    const tooltipRef = useRef<HTMLDivElement>(null);
    const history = useHistory()

    const { data = [], isFetched } = useQuery<StatByDocumentsType[]>({
        queryKey: [ENTITY, 'statByDocument', params],
        async queryFn() {
            const { data } = await fetchStatByDocumentTypes({ params })
            return (data || [])?.map(StatByDocument) || []
        },
        initialData: [],
        retry: false,
        refetchOnWindowFocus: false,
    })

    const initRef = useCallback((node: HTMLCanvasElement) => {
        if (!node) return;
        if (chartRef.current) {
            chartRef.current.destroy();
        }
        chartRef.current = new Chart(node, {
            type: 'bar',
            data: {
                datasets: [
                    {
                        label: t("stat_by_document_status"),
                        data: data.map(({type, ...item}) => ({
                            type: t(type),
                            ...item
                        })),
                        borderRadius: 4,
                        barPercentage: 0.2,
                    }
                ]
            },
            options: {
                onClick(_, elements: any) {
                    if (elements.length > 0) {
                        const chartElement = elements[0];
                        const dataIndex = chartElement.index;

                        history.push(`/stat-by-document?document_type=${data[dataIndex].type}`);
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true
                    }
                },
                parsing: {
                    xAxisKey: 'type',
                    yAxisKey: 'total'
                },
                plugins: {
                    tooltip: {
                        enabled: false,
                        external: (context: any) => {
                            const tooltipModel = context.tooltip;
                            const tooltipEl = tooltipRef.current;

                            if (!tooltipEl) return;

                            if (tooltipModel.opacity === 0) {
                                tooltipEl.style.opacity = '0';
                                return;
                            }

                            let innerHtml = ``;

                            tooltipModel.dataPoints.forEach(point => {
                                point.raw.statuses?.map(status => {
                                    innerHtml += `<div class="${classes.tooltipItem}">${t(status.status)}: <strong>${status.count}</strong></div>`;
                                })
                            })

                            tooltipEl.innerHTML = innerHtml;

                            tooltipEl.style.opacity = '1';
                            tooltipEl.style.left = tooltipModel.caretX + 'px';
                            tooltipEl.style.top = tooltipModel.caretY + 'px';
                        }
                    }
                }
            }
        })
    }, [data, i18n.language]);

    if (!isFetched) {
        return (
            <div className={classes.wrapper}>
                <Skeleton height={200} />
            </div>
        )
    }

    return (
        <div className={classes.wrapper}>
            <div
                ref={tooltipRef}
                className={classes.tooltip}
            />
            <canvas className={classes.wrapper} ref={initRef}></canvas>
        </div>
    )
}
